import { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { usePermissions } from '../hooks/usePermissions'
import {
  ChevronRight,
  ChevronDown,
  Pin,
  PinOff,
  TrendingUp,
} from 'lucide-react'
import { Button } from './ui/Button'
import { BrandMark } from './ui/BrandMark'
import { BRAND_NAME } from '../config/brand'
import { useBillingConfig, useSubscriptionQuery } from '../hooks/useBillingConfig'
import { useIntegration } from '../hooks/useIntegrations'
import { avatarInitial } from '../utils/avatarInitial'
import { buildNavModel, isLinkActive, SYSTEM_ICON, type NavLink, type NavRow } from './sidebarNav'

const SIDEBAR_PINNED_KEY = 'sidebar:pinned'

/**
 * Lit la préférence d'épinglage. Épinglée par défaut : toute valeur absente ou
 * illisible retombe sur `true` (et non sur l'ancien comportement replié).
 * Lazy initializer de `useState` → un seul accès au stockage, au montage.
 */
function readPinnedPreference(): boolean {
  try {
    const stored = localStorage.getItem(SIDEBAR_PINNED_KEY)
    return stored === null ? true : JSON.parse(stored) === true
  } catch {
    return true
  }
}

/**
 * UNE SEULE RANGÉE DE NAVIGATION (LOT-H11, DA-029 + audit §4).
 *
 * Groupes et entrées partagent la MÊME géométrie et le MÊME cran typographique
 * (`text-t4`) : l'audit relevait « libellés de groupe en capitales +
 * interlettrage, libellés d'entrée en casse normale — deux registres
 * typographiques dans un même composant ». La hiérarchie passe désormais par
 * l'icône, le retrait et la couleur, pas par une deuxième échelle de texte.
 *
 * `min-h-touch` : la recette a mesuré les 7 boutons de groupe à 223 × 42 px —
 * 42 px est la hauteur DESSINÉE du §3, pas la cible TACTILE de 44 px, et ici
 * c'était bien la cible qui faisait 42.
 */
const NAV_ROW =
  'w-full flex items-center gap-3 px-3 min-h-touch rounded-control text-t4 transition-colors'

/**
 * ÉTAT ACTIF — pilule pleine, JAMAIS rognée (audit §4).
 *
 * L'entrée active portait `border-l-4` sur un conteneur `rounded-xl` en
 * `overflow-hidden` : le filet carré mangeait le coin arrondi et la pilule
 * paraissait coupée par le bord de la colonne. Le marqueur est maintenant le
 * fond lui-même, avec un anneau INTERNE (`ring-inset`) qui ne déborde donc
 * d'aucun côté.
 */
const NAV_ROW_ACTIVE = 'bg-primary-500/15 text-foreground ring-1 ring-inset ring-primary-500/30'
const NAV_ROW_IDLE = 'text-foreground-secondary hover:bg-background-secondary/60 hover:text-foreground'
/**
 * Rangée-PONT dont une sous-entrée est la page ouverte (L-NAV-2) : la rubrique
 * se lit (texte plein) sans se dire « page courante » — la pilule appartient à
 * la sous-entrée.
 */
const NAV_ROW_SECTION = 'text-foreground hover:bg-background-secondary/60'
/**
 * Sous-entrée d'un pont, ou entrée du groupe « Système » : SANS icône, texte
 * aligné sur celui de la rangée parente (12 px de marge + 18 px d'icône +
 * 12 px d'écart = 42 px), ~120 px de libellé à 192 px de colonne.
 *
 * 40 px de haut (recette banc 1024 × 768) : à 44 px, le super-admin « Système »
 * ouvert empilait 14 rangées (696 px pour 628 visibles). Les rangées de
 * premier niveau et le bouton de groupe gardent la cible de 44 px (`NAV_ROW`).
 */
const NAV_SUB_ROW =
  'w-full flex items-center gap-3 pl-[42px] pr-3 min-h-[40px] rounded-control text-t4 transition-colors'

export default function Sidebar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation(['shell', 'common'])
  const { isSystemAdmin, hasPermission, user, isAgent } = usePermissions()
  // #270 : intégrations actives de l'organisation (« Historique donateur »).
  const allodons = useIntegration('allodons')
  // 📌 Épinglée par DÉFAUT (produit tactile : le survol n'existe pas sur iPad, une
  // sidebar repliée coûtait ~3 taps par navigation) et état PERSISTÉ entre les
  // sessions : l'agent ne redéplie plus la barre à chaque ouverture de l'app.
  const [pinned, setPinned] = useState<boolean>(readPinnedPreference)
  const [hovering, setHovering] = useState(false)
  const sidebarRef = useRef<HTMLDivElement>(null)

  /**
   * LOT-BILLING-RT (R2) — visibilite du CTA « Passer à Pro ».
   *
   * Le bouton n'apparait que si le tunnel est REELLEMENT proposable :
   *   - flag `billing_enabled` ALLUME (eteint → aucune entree de nav billing,
   *     meme regle que l'etat « Offre non activee » de BillingRoute) ;
   *   - role USER ou ADMIN (l'AGENT n'a pas la main sur la facturation) ;
   *   - PAS de souscription deja active (un admin abonne n'a rien a acheter —
   *     au test reel il a pu payer une seconde fois pour rien).
   * La requete souscription n'est lancee que lorsque les deux premieres
   * conditions tiennent (`enabled`), et le bouton n'apparait qu'une fois la
   * reponse connue : jamais de CTA « flash » pour un abonne.
   */
  //
  // Retouche client (#288) : le compte SYSTÈME (super-admin, rôle ADMIN sur le
  // tenant Système) n'achète rien — jamais de CTA, et sa souscription n'est
  // pas interrogée ; et pas de CTA sur l'écran de TÉLÉPHONIE (l'agent ou
  // l'admin y travaille, ce n'est pas le lieu d'une offre). Menu replié : le
  // pied de colonne n'est pas rendu, donc pas de CTA non plus.
  const { data: billingConfig } = useBillingConfig()
  const billingEnabled = billingConfig?.billingEnabled === true
  const upgradeEligibleRole = (user?.role === 'USER' || user?.role === 'ADMIN') && !isSystemAdmin
  const onTelephonyScreen = location.pathname.startsWith('/v2/softphone')
  const subscriptionQuery = useSubscriptionQuery(billingEnabled && upgradeEligibleRole)
  const showUpgradeCta =
    billingEnabled &&
    upgradeEligibleRole &&
    !onTelephonyScreen &&
    subscriptionQuery.isSuccess &&
    subscriptionQuery.data?.status !== 'active'

  // Persister le choix de l'utilisateur. `localStorage` peut lever (Safari en
  // navigation privée, quota) : l'échec ne doit jamais casser la navigation.
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_PINNED_KEY, JSON.stringify(pinned))
    } catch {
      // Préférence non persistée — sans conséquence sur la session en cours.
    }
  }, [pinned])

  // ✅ Auto-collapse quand on clique en dehors de la sidebar (sauf si pinned)
  // FIX: Utiliser 'click' au lieu de 'mousedown' pour mieux gérer les interactions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Toujours fermer si on clique en dehors, sauf si pinned
      if (!pinned && sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setHovering(false)
        setExpandedSections([]) // Fermer aussi les sous-menus
      }
    }
    // Utiliser 'click' pour que ça se déclenche après les interactions internes
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [pinned])
  const [expandedSections, setExpandedSections] = useState<string[]>([])

  /**
   * GROUPES REPLIABLES — il n'en reste qu'un, « Système » (super-admin), depuis
   * le menu à plat (L-NAV-2). Le mécanisme multi-ouvert de LOT-H11 est gardé
   * tel quel : il ne coûte rien et resservira si un deuxième groupe revient.
   */
  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    )
  }

  const handleLinkClick = () => {
    // Ne plus épingler automatiquement - l'utilisateur doit cliquer sur le bouton pin
    if (!pinned) {
      setHovering(false) // Ferme après navigation
    }
  }

  // L-NAV-2 — le menu à plat, décrit (droits compris) dans `sidebarNav.ts`.
  const nav = buildNavModel({
    hasPermission,
    isSystemAdmin,
    isAgent,
    role: user?.role,
    allodonsEnabled: allodons.enabled,
  })
  const pathname = location.pathname
  const isActive = (link: NavLink) => isLinkActive(link, pathname)
  const rowInSection = (row: NavRow) => isActive(row) || row.children.some(isActive)

  // Le groupe « Système » (super-admin) est replié par défaut, et s'ouvre SEUL
  // quand la page courante en fait partie — sans cela l'entrée active resterait
  // invisible tant qu'on n'a pas rouvert le groupe à la main.
  const systemActive = nav.system.some(isActive)
  useEffect(() => {
    if (!systemActive) return
    setExpandedSections(prev => (prev.includes('system') ? prev : [...prev, 'system']))
  }, [systemActive])

  // La sidebar est "effectivement ouverte" si elle est épinglée OU si on survole
  const isEffectivelyOpen = pinned || hovering

  const systemOpen = isEffectivelyOpen ? expandedSections.includes('system') : false

  /**
   * La LISTE défile seule (`flex-1 min-h-0 overflow-y-auto`) ; en-tête et pied
   * (identité, version) restent fixes. Quand elle déborde (super-admin,
   * « Système » ouvert à 1024 × 768), l'entrée de la page courante est amenée à
   * l'écran au montage, à chaque changement de route et quand un groupe
   * s'ouvre — en recette banc, la dernière entrée de « Système » (« Outils MCP »,
   * page retirée depuis, #296) restait cachée sur sa propre page.
   */
  const navRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const current = navRef.current?.querySelector<HTMLElement>('[aria-current="page"]')
    if (current && typeof current.scrollIntoView === 'function') {
      current.scrollIntoView({ block: 'nearest' })
    }
  }, [pathname, systemOpen, isEffectivelyOpen])

  /** Sous-entrée d'un pont, ou entrée du groupe « Système ». */
  const renderSubRow = (link: NavLink) => {
    const active = isActive(link)
    return (
      <Link
        key={link.href}
        to={link.href}
        onClick={handleLinkClick}
        aria-current={active ? 'page' : undefined}
        data-nav-row="sub"
        data-nav-label={link.labelKey}
        className={`${NAV_SUB_ROW} ${active ? NAV_ROW_ACTIVE : NAV_ROW_IDLE}`}
        title={t(link.labelKey)}
      >
        <span className="truncate">{t(link.labelKey)}</span>
      </Link>
    )
  }

  /**
   * Une rangée du menu à plat. Ouverte, la colonne montre la pilule sur la
   * page exacte et, pour un pont, ses sous-entrées quand on est dans la
   * rubrique. Repliée (icônes seules), la rangée de la rubrique porte la
   * pilule et les sous-entrées se taisent.
   */
  const renderRow = (row: NavRow) => {
    const Icon = row.icon
    const active = isActive(row)
    const inSection = rowInSection(row)
    const tone = !isEffectivelyOpen
      ? (inSection ? NAV_ROW_ACTIVE : NAV_ROW_IDLE)
      : active ? NAV_ROW_ACTIVE : inSection ? NAV_ROW_SECTION : NAV_ROW_IDLE
    return (
      <div key={row.href} className="mb-0.5">
        <Link
          to={row.href}
          onClick={handleLinkClick}
          aria-current={active ? 'page' : undefined}
          data-nav-row="top"
          data-nav-label={row.labelKey}
          data-nav-current={!active && inSection ? 'section' : undefined}
          className={`${NAV_ROW} ${tone} ${!isEffectivelyOpen ? 'justify-center' : ''}`}
          title={!isEffectivelyOpen ? t(row.labelKey) : undefined}
        >
          <Icon size={18} className="shrink-0" aria-hidden="true" />
          {isEffectivelyOpen && <span className="truncate">{t(row.labelKey)}</span>}
        </Link>
        {isEffectivelyOpen && inSection && row.children.length > 0 && (
          <div className="mt-0.5 space-y-0.5">{row.children.map(renderSubRow)}</div>
        )}
      </div>
    )
  }

  return (
    <>
      {/* Barre latérale — surface NEUTRE, accent `primary` (LOT-H11, DA-015).
          Le fond dégradé et les halos bleu→violet→rose de la version d'origine
          sont retirés : la recette comptait 12 classes violettes et 19 dégradés
          portés par le châssis, donc répétés sur les 24 écrans. */}
      <div
        ref={sidebarRef}
        onMouseEnter={() => !pinned && setHovering(true)}
        onMouseLeave={() => !pinned && setHovering(false)}
        className={`fixed top-0 left-0 h-screen flex flex-col bg-background-primary/95 border-r border-border backdrop-blur-2xl transition-all duration-300 ease-in-out ${
          isEffectivelyOpen ? 'w-48 lg:w-56' : 'w-14 lg:w-16'
        } ${hovering ? 'z-[60] border-primary-500/40 shadow-2xl' : 'shadow-2xl z-40'}`}
      >
        {/* En-tête de colonne : marque + épinglage */}
        <div className="shrink-0 flex items-center justify-between gap-2 px-3 py-3 border-b border-border">
          {isEffectivelyOpen ? (
            <div className="flex items-center gap-3 min-w-0">
              {/* Icône de marque iCall CRM (coins arrondis déjà dans le SVG). */}
              <BrandMark size={40} />
              <div className="min-w-0">
                <div className="text-t3 text-foreground truncate">
                  {BRAND_NAME}
                </div>
                {/* Numéro de version : un fait, donc neutre (§1.2) — il était en
                    vert, c'est-à-dire dans le token `success`. */}
                <div className="text-t6 uppercase text-foreground-tertiary truncate">
                  iPAD v2.2
                </div>
              </div>
            </div>
          ) : (
            /* Repliée, la colonne n'offre plus aucun bouton d'épinglage : la
               tuile de marque est le seul moyen de la rouvrir AU DOIGT (le
               survol n'existe pas sur iPad). Elle est donc un vrai bouton,
               nommé, à 44 px — l'ancienne pastille du bas de colonne rendait ce
               service sans nom accessible ni cible suffisante. */
            <button
              type="button"
              onClick={() => setHovering(true)}
              aria-label={t('sidebar.expand')}
              title={t('sidebar.expand')}
              className="mx-auto flex h-touch w-touch items-center justify-center rounded-control"
            >
              <BrandMark size={32} />
            </button>
          )}
          {isEffectivelyOpen && (
            /* 📌 DA-029 — 26 × 26 px SANS NOM ACCESSIBLE en tête de colonne.
               Cible portée à 44 px (`h-touch w-touch`) et `aria-label` explicite :
               un `title` ne nomme pas un bouton pour un lecteur d'écran. */
            <button
              type="button"
              onClick={() => setPinned(!pinned)}
              className={`shrink-0 flex h-touch w-touch items-center justify-center rounded-control border transition-colors ${
                pinned
                  ? 'bg-primary-500/15 border-primary-500/40 text-primary-400 hover:bg-primary-500/25'
                  : 'bg-background-secondary border-border-secondary text-foreground-secondary hover:text-foreground'
              }`}
              aria-label={pinned ? t('sidebar.unpinTooltip') : t('sidebar.pinTooltip')}
              aria-pressed={pinned}
              title={pinned ? t('sidebar.unpinTooltip') : t('sidebar.pinTooltip')}
            >
              {pinned ? <PinOff size={16} aria-hidden="true" /> : <Pin size={16} aria-hidden="true" />}
            </button>
          )}
        </div>

        {/* Navigation défilante. `flex-1 min-h-0` remplace le
            `h-[calc(100vh-145px)]` d'origine : la hauteur se déduit désormais du
            châssis lui-même, elle ne se recalcule plus à la main à chaque
            retouche de l'en-tête ou du pied. */}
        {/* La gouttière latérale est portée par la COLONNE (`px-2`), pas par
            chaque rangée : avec `w-full mx-2` la pilule active mesurait 8 px de
            plus que la colonne et passait par-dessus la bordure droite — mesuré
            au navigateur. C'est l'autre moitié de « pilule active non rognée » :
            elle ne doit ni être coupée à gauche, ni déborder à droite. */}
        <nav ref={navRef} className="flex-1 min-h-0 overflow-y-auto px-2 py-3 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
          {nav.work.map(renderRow)}
          {nav.work.length > 0 && nav.organisation.length > 0 && <NavSeparator />}
          {nav.organisation.map(renderRow)}

          {/* SYSTÈME — le SEUL groupe repliable, réservé au super-admin. */}
          {nav.system.length > 0 && (
            <>
              <NavSeparator />
              <button
                type="button"
                onClick={() => isEffectivelyOpen && toggleSection('system')}
                aria-expanded={isEffectivelyOpen ? systemOpen : undefined}
                data-nav-row="group"
                data-nav-label="nav.sections.system"
                className={`${NAV_ROW} ${!isEffectivelyOpen ? 'justify-center' : 'justify-between'} ${
                  !isEffectivelyOpen && systemActive ? NAV_ROW_ACTIVE : NAV_ROW_IDLE
                }`}
                title={!isEffectivelyOpen ? t('nav.sections.system') : undefined}
              >
                <span className="flex items-center gap-3 min-w-0">
                  <SYSTEM_ICON size={18} className="shrink-0" aria-hidden="true" />
                  {isEffectivelyOpen && <span className="truncate">{t('nav.sections.system')}</span>}
                </span>
                {isEffectivelyOpen && (
                  systemOpen ? (
                    <ChevronDown size={14} className="shrink-0 opacity-70" aria-hidden="true" />
                  ) : (
                    <ChevronRight size={14} className="shrink-0 opacity-70" aria-hidden="true" />
                  )
                )}
              </button>
              {systemOpen && isEffectivelyOpen && (
                <div className="mt-0.5 space-y-0.5">{nav.system.map(renderSubRow)}</div>
              )}
            </>
          )}
        </nav>

        {/* Pied de colonne — informations utilisateur */}
        {isEffectivelyOpen && user && (
          <div className="shrink-0 border-t border-border bg-background-primary">
            {/* Passer à Pro — flag allumé + USER/ADMIN sans souscription
                active (LOT-BILLING-RT R2, conditions calculées plus haut).
                Recette banc : le gros bouton plein portait le pied de 71 à
                140 px et faisait défiler le menu admin à 1024 × 768. Une ligne
                compacte suffit : bouton `sm` (32 px dessinés, cible tactile de
                44 px par sa zone de frappe), fantôme, ~37 px avec sa marge. */}
            {showUpgradeCta && (
              <div className="px-2 py-0.5 border-b border-border">
                <Button variant="ghost" size="sm" icon={TrendingUp} onClick={() => navigate('/upgrade')} fullWidth>
                  {t('sidebar.upgradeToPro')}
                </Button>
              </div>
            )}

            {/* User Info */}
            <div className="p-3">
              <div className="flex items-center gap-3">
                {/* Avatar : aplat `primary` (il était en dégradé bleu→violet).
                    L-NAV-1 : même initiale que le bandeau (le nom, pas l'e-mail). */}
                <div className="w-10 h-10 shrink-0 rounded-control bg-primary-600 flex items-center justify-center text-white text-t3">
                  {avatarInitial(user.name, user.email)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-t4 text-foreground truncate">
                    {user.name || user.email}
                  </div>
                  {/* 🎭 BADGE DE RÔLE NEUTRE (DA-015 / §1.2). « Super Admin »
                      était violet et « Agent » vert : un rôle est un FAIT, ni un
                      succès ni un avertissement. La hiérarchie passe par le
                      libellé, pas par une teinte — et le violet du châssis
                      s'affichait sur les 24 écrans. */}
                  <div className="mt-0.5">
                    <span className="inline-flex items-center rounded-pill border border-border-secondary bg-background-secondary px-2 py-0.5 text-t6 uppercase text-foreground-secondary">
                      {isSystemAdmin ? t('common:roles.superAdmin') :
                       user.role === 'ADMIN' ? t('common:roles.admin') :
                       user.role === 'AGENT' ? t('common:roles.agent') : t('common:roles.user')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cale qui pousse le contenu — elle suit EXACTEMENT la largeur de la
          colonne (elle valait `w-56` quand la barre en faisait 48 sous 1024 px,
          d'où un décalage de 32 px sur iPad portrait). */}
      <div
        className={`${
          isEffectivelyOpen ? 'w-48 lg:w-56' : 'w-14 lg:w-16'
        } flex-shrink-0 transition-all duration-300`}
      />
    </>
  )
}

/** Séparateur du menu : travail │ organisation │ système. */
function NavSeparator() {
  return <div role="separator" className="my-2 border-t border-border" />
}
